import _ from "lodash";
import { DateTime } from "luxon";
import { action, makeObservable } from "mobx";
import { inject, observer } from "mobx-react";
import React from "react";

import config from "../../config";
import { formatDateTime, formatTime } from "../../shared/dateTimeUtils";
import Icon from "../../shared/Icon";
import Loader from "../../shared/Loader/Loader";
import Swal from "../../shared/Swal";
import Toggle from "../../shared/Toggle";
import { MODULE_ID } from "../../shared/moduleIds";
import className from "../../shared/className";
import AbstractPatients from "./AbstractPatients";
import "./patients.scss";
import PatientStore from "./PatientStore";
import SnippetReport from "../SnippetReport";
import ObPatient from "./ObPatient";
import SearchForm from "./components/SearchForm";

class Patients extends AbstractPatients {
  constructor(props) {
    super(props);

    makeObservable(this, {
      listChange: action,
      selectPatient: action,
    });

    // observe hashes routes.
    if (props.location.hash) {
      props.history.push(props.location.hash.replace(/^#/, ""));
      this.listChange = () => {};
    }

    this.props.store.monitorStore = undefined;
    if (props.store.patientStore) {
      this.store = props.store.patientStore;
      this.store.history = props.history;
      this.store.restorePatientList({
        listIndex: props.match?.params?.listIndex,
      });
    } else {
      this.store = new PatientStore({
        listIndex: props.match?.params?.listIndex,
        history: props.history,
        appStore: props.store,
      });
    }
    this.store.updatedCounts = new Set();

    props.store.patientStore = this.store;
    props.store.hideMainNav = false;
    this.Patient = this.Patient.bind(this);
    this.listChange = this.listChange.bind(this);
    this.openGroupSort = this.openGroupSort.bind(this);
    this.searchChange = this.searchChange.bind(this);
    this.searchClear = this.searchClear.bind(this);
    this.openUnitFilter = this.openUnitFilter.bind(this);
    this.resetSearch = this.resetSearch.bind(this);
    this.shouldShowFilterMessage = this.shouldShowFilterMessage.bind(this);
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    window.clearInterval(this.store.refreshIntervalId);
  }

  componentDidUpdate() {
    // This filtering has to be done after render, because it depends on the text output, not the data provided.
    this.applySimpleSearch();

    // Restore the scroll position AFTER the render is completed
    if (
      !this.store.loadingConfig &&
      !this.store.multiPatientNotify &&
      this.store.refName
    ) {
      const ele = this[this.store.refName];
      if (ele) {
        this.restoreScrollPosition();
        this.restoreScrollPosition = () => {};
      }
    }
  }

  componentDidMount() {
    this.store.fetchListCounts();
    this.restoreScrollPosition();
  }

  shouldShowFilterMessage() {
    if (!this.store.unitFilterEnabled) return false;
    const {
      numPatientsHiddenBySearchQuery,
      _patients,
      filteredPatients,
      searchQuery,
    } = this.props.store.patientStore;

    const searchQueryIsNotEmpty =
      searchQuery !== "" && searchQuery !== undefined;

    return (
      (searchQueryIsNotEmpty &&
        !numPatientsHiddenBySearchQuery &&
        _patients?.length > filteredPatients?.length) ||
      (numPatientsHiddenBySearchQuery &&
        _patients?.length > filteredPatients?.length)
    );
  }

  render() {
    let multiPatientOption = this.refMultiPatientOption;
    if (
      this.store.selectedListOption?.value?.moduleId === MODULE_ID.OBSTETRICS
    ) {
      multiPatientOption = this.refObMultiPatientOption;
    }
    return (
      <div className="patients widget" id="patients">
        {this.SubNav({
          disableMultiView: !this.store.multiViewEnabled,
        })}
        <div className="patients-body">
          <div className="sidebar">
            <div className="site-name">{config.siteName}</div>
            {this.store.loadingConfig ? (
              <Loader />
            ) : (
              this.store.listOptions.map((option, i) =>
                option.disabled ? (
                  <div className="menu-header" key={i}>
                    {option.label}
                  </div>
                ) : (
                  this.renderRow(option, i)
                ),
              )
            )}
          </div>
          {_.get(this.store.selectedListOption, "value.type") ===
          "snippets_reports" ? (
            <SnippetReport patientsStore={this.store} />
          ) : (
            <div
              className="patient-groups"
              ref={(r) => (this.refPatientGroups = r)}
              id="patient-groups"
            >
              {this.shouldShowFilterMessage() === true && (
                <div className="patient-filter-message">
                  Modify Unit Filters to see more search results.
                </div>
              )}
              <SearchForm
                key={`${this.store.listIndex}_${this.store.searchFormKey}`}
                searchConfig={this.store.searchConfig}
                searchParams={this.store.searchParams}
                parent={this}
                search={this.search}
                resetSearch={this.resetSearch}
              />
              <this.SnippetWorklistToggles />

              {this.store.loading ? (
                <Loader />
              ) : this.store.groups ? (
                this.store.groupsWithPatients.map((group, i) => {
                  const patients = group.patients;
                  return (
                    <React.Fragment key={i}>
                      <header
                        className={this.cn({
                          active: group.active,
                          "single-column":
                            this.store.isWorklist && this.store.singleColumn,
                          "filtered-out":
                            patients.filter((p) => p.isFilteredOut).length ===
                            patients.length,
                        })}
                        ref={`header-${i}`}
                      >
                        {this.store?.groupByValue?.value !== "NONE" && (
                          <span className="label">
                            {(group.label || group.label === 0) &&
                            group.label !== "--"
                              ? group.label
                              : "Not Documented"}
                          </span>
                        )}
                        <span className="sub-label">{group.sublabel}</span>
                      </header>
                      <div
                        className={this.cn({
                          patients: 1,
                          active: group.active,
                          "single-column":
                            this.store.isWorklist && this.store.singleColumn,
                          "filtered-out":
                            patients.filter((p) => p.isFilteredOut).length ===
                            patients.length,
                        })}
                      >
                        {patients.map((p) => this.Patient(p, i, group.groupId))}
                      </div>
                    </React.Fragment>
                  );
                })
              ) : this.store.patients ? (
                <div
                  {...this.className({
                    patients: 1,
                    "single-column":
                      this.store.isWorklist && this.store.singleColumn,
                  })}
                >
                  {this.store.patients.map(this.Patient)}
                </div>
              ) : null}
              {!this.store.loading &&
              !this.store.loadingConfig &&
              this.store._patients?.length === 0 &&
              !this.store.noSearchRequirements &&
              !this.store.selectedListOption?.value?.type ===
                "snippets_reports" ? (
                <div className="warning">No Results for Search Criteria</div>
              ) : null}
              <div
                className="warning simple-search-no-results"
                ref={(r) => (this.refNoResults = r)}
              >
                No Results for Search Criteria
              </div>
            </div>
          )}
        </div>
        {this.store.multiPatientNotify && multiPatientOption ? (
          <div
            className="popover"
            style={{
              position: "absolute",
              zIndex: 2,
              top: multiPatientOption.offsetTop,
              left:
                multiPatientOption.offsetLeft + multiPatientOption.clientWidth,
            }}
          >
            Patient Added
          </div>
        ) : null}
      </div>
    );
  }

  renderRow(option, i) {
    const isMultiPatient = option.value.type === "multi_patient";
    const isObMultiPatient = option.value.type === "ob_multi_patient";
    const count = option.value.count;
    const multiPatientCount = _.get(
      this.props.store.prefs,
      isObMultiPatient
        ? "userPreferences.client.ob.multiPatients.length"
        : "userPreferences.client.pm.multiPatients.length",
    );
    return (
      <div
        key={i}
        role="link"
        className={this.cn({
          "menu-option": 1,
          selected: this.store.isSelected(option),
          notify: isMultiPatient && this.store.multiPatientNotify,
        })}
        ref={(el) => {
          if (isMultiPatient) {
            this.refMultiPatientOption = el;
          } else if (isObMultiPatient) {
            this.refObMultiPatientOption = el;
          }
        }}
        onClick={() => this.listChange(option)}
      >
        {option.label}
        {count ? <div className="count">{count}</div> : null}
        {option.value.type.search("multi_patient") !== -1 &&
        multiPatientCount ? (
          <div className="count">{multiPatientCount}</div>
        ) : null}
      </div>
    );
  }

  SnippetWorklistToggles = () => {
    if (!this.store.isWorklist) return null;
    return (
      <div className="work-list-toggles">
        <Toggle
          className="single-column-control"
          label={<Icon symbol="list" />}
          onChange={() => {
            this.store.singleColumn = !this.store.singleColumn;
            this.props.store.setPref(
              "pm.workList.singleColumn",
              this.store.singleColumn,
            );
          }}
          checked={!!this.store.singleColumn}
        />
        {this.store.isSnippetWorklist ? (
          <Toggle
            className="hide-completed-control"
            label={"Hide Completed"}
            onChange={() => {
              this.store.hideCompleted = !this.store.hideCompleted;
              this.props.store.setPref(
                "pm.workList.hideCompleted",
                this.store.hideCompleted,
              );
            }}
            checked={!!this.store.hideCompleted}
          />
        ) : null}
      </div>
    );
  };

  restoreScrollPosition() {
    if (this.store.refName) {
      const ele = this[this.store.refName];
      if (ele) {
        requestAnimationFrame(() => {
          ele.scrollIntoView();
        });
      }
    }
  }

  searchChange(e) {
    this.store.searchQuery = e.target.value.trim();
  }

  applySimpleSearch() {
    if (!this.refPatientGroups) return;
    const patientCells = this.refPatientGroups.querySelectorAll(".patient");
    if (!patientCells) return;
    let numHidden = 0;

    _.each(patientCells, (_patientCell) => {
      const patientCell = _patientCell.cloneNode(true);
      const dropdown = patientCell.querySelector(".drop-down");
      dropdown && dropdown.remove();

      if (patientCell) {
        let hideCell = false;
        if (this.store.searchQuery) {
          const group = _patientCell.closest(".patients");
          const header =
            _.get(group, "previousSibling.tagName") === "HEADER" &&
            group.previousSibling;
          const headerText = header ? header.textContent : "";
          const patientText = (
            patientCell.textContent + headerText
          ).toLowerCase();
          const searchQueryTerms = this.store.searchQuery.split(" ");
          const termsFound = searchQueryTerms.reduce((arr, term) => {
            arr.push(patientText.indexOf(term.toLowerCase()) > -1);
            return arr;
          }, []);
          hideCell = termsFound.includes(false);
        }
        _patientCell.style.display = hideCell ? "none" : "";
        numHidden += hideCell ? 1 : 0;
      }
    });

    this.props.store.patientStore.numPatientsHiddenBySearchQuery = numHidden;

    // Hide group header, if all patients are filtered.
    if (!this.store.isSnippetWorklist) {
      _.each(this.refPatientGroups.querySelectorAll("header"), (header) => {
        const group = header.nextElementSibling;
        if (
          !_.get(group, "classList") &&
          _.get(group, "classList").contains("patients")
        )
          return;

        const hasVisiblePatients = _.find(
          group.querySelectorAll(".patient"),
          (patient) => patient.style.display !== "none",
        );

        if (!hasVisiblePatients) {
          header.style.display = "none";
        } else {
          header.style.display = "";
        }
      });
    }

    // Display error message, if all patients are filtered.
    let { filterUnits, hiddenUnits, searchQuery } =
      this.props.store.patientStore;
    let allFiltersOff =
      filterUnits && hiddenUnits && filterUnits.length === hiddenUnits.length;
    let showMsg = false;
    if ((patientCells?.length || allFiltersOff) && searchQuery?.length) {
      showMsg = !_.find(patientCells, (p) => p.style.display !== "none");
    }
    this.refNoResults.style.display = showMsg ? "flex" : "none";
  }

  Patient(patient, i, groupId = "") {
    if (!patient) return null;
    const moduleId = this.store?.selectedListOption?.value?.moduleId;
    const moduleSpecificData = patient?.moduleSpecificData?.find(
      (moduleData) => moduleData.moduleId === moduleId,
    );
    if (moduleId === MODULE_ID.GLOBAL)
      return this.notificationPatient(patient, i);
    if (moduleId === MODULE_ID.CARDIOLOGY)
      return this.cardioPatient({ patient, i, moduleSpecificData });
    if (moduleId === MODULE_ID.OBSTETRICS)
      return (
        <ObPatient
          patient={patient}
          meta={moduleSpecificData}
          appStore={this.props.store}
          parent={this}
          key={patient.asmpid}
        />
      );
    return this.defaultPatient(patient, i, moduleSpecificData, groupId);
  }

  notificationPatient(patient) {
    const acquisitionDate =
      patient.notificationData &&
      DateTime.fromISO(patient.notificationData[0].sentDate);

    return (
      <div
        className={this.patientClass(patient)}
        key={patient.asmpid}
        ref={(r) => (this[`ref-patientCell-${patient.asmpid}`] = r)}
        onClick={() =>
          this.selectPatient(
            patient,
            "",
            `/ecgs/${patient.notificationData?.[0]?.ecgId ?? ""}`,
          )
        }
      >
        <div className="name">
          {patient.lastName}, {patient.firstName}
          <span className="info">
            {" "}
            • {patient.gender[0]} • {patient.age}
          </span>
          {acquisitionDate
            ? [
                <div className="info" key={patient.asmpid + "acquisitionDate"}>
                  {formatDateTime(acquisitionDate, "-")}
                </div>,
                <div className="info" key={patient.asmpid + "message"}>
                  {patient.notificationData[0].message}
                </div>,
              ]
            : null}
        </div>
      </div>
    );
  }

  cardioPatient({ patient, moduleSpecificData }) {
    const { asmpid, firstName, lastName, gender, age } = patient;
    const aDate = DateTime.fromISO(moduleSpecificData?.acquisitionDate);

    return patient ? (
      <div
        className={`cardio ${this.patientClass(patient)}`}
        key={asmpid}
        ref={(r) => (this[`ref-patientCell-${asmpid}`] = r)}
        onClick={() => this.selectPatient(patient)}
      >
        {moduleSpecificData?.count ? (
          <div className="meta">{moduleSpecificData?.count}</div>
        ) : null}
        <div className="name">
          {lastName}, {firstName}
          <span className="info">
            {" "}
            • {gender[0]} • {age}
          </span>
        </div>
        <div className="info">{formatDateTime(aDate.toISO(), "-")}</div>
        {moduleSpecificData?.diagnosis ? (
          <div className="info">{moduleSpecificData?.diagnosis}</div>
        ) : null}
      </div>
    ) : null;
  }
  defaultPatient(patient, groupId) {
    const { moduleSpecifics } = patient;
    const snippetUpdateTime = moduleSpecifics?.snippetWorklistItemUpdated;
    return (
      <div
        className={this.patientClass(patient, groupId)}
        key={groupId + patient.asmpid}
        ref={(r) => (this[`ref-patientCell-${patient.asmpid}-${groupId}`] = r)}
        onClick={() => this.selectPatient(patient, groupId)}
      >
        {moduleSpecifics?.moduleId?.toString() ===
          MODULE_ID.PATIENT_MONITORING.toString() && !this.store.isWorklist ? (
          <div className="drop-down" onClick={(e) => e.stopPropagation()}>
            <div className="options">
              {moduleSpecifics.isArchived ? (
                <a
                  className="option"
                  onClick={this.aClick}
                  href={`/patient/${patient.asmpid}/monitor${
                    patient.lastActive ? `/history/${patient.lastActive}` : ""
                  }`}
                >
                  Monitor
                </a>
              ) : (
                <a
                  className="option"
                  onClick={this.aClick}
                  href={`/patient/${patient.asmpid}/monitor/events`}
                >
                  Events
                </a>
              )}
              <a
                className="option"
                onClick={this.aClick}
                href={`/patient/${patient.asmpid}/overview`}
              >
                Overview
              </a>
              {!config.mainNav || config.mainNav.includes("ventilator") ? (
                <a
                  className="option"
                  onClick={this.aClick}
                  href={`/patient/${patient.asmpid}/ventilator`}
                >
                  Ventilator
                </a>
              ) : null}
            </div>
          </div>
        ) : null}
        <div
          className={this.cn({
            "meta location": true,
            "status-active":
              moduleSpecifics?.moduleId?.toString() ===
                MODULE_ID.PATIENT_MONITORING.toString() &&
              patient.status === "active" &&
              !this.store.isSnippetWorklist,
          })}
        >
          {moduleSpecifics?.location}
        </div>
        <MyPatientsToggle
          patient={patient}
          parent={this}
          appStore={this.props.store}
        />
        <div className="name">
          {patient.lastName}, {patient.firstName}
          <span className="info">
            • {patient.gender ? patient.gender[0] : " "} •{" "}
            {this.orBlank(patient.age)}
          </span>
        </div>
        <div className="info">
          <span>
            {this.orBlank(patient.mrn)} • DOB:{" "}
            {this.orBlank(patient.dateOfBirth)}
          </span>
        </div>
        <div className="notconnected">
          {patient.isEmptyBed ? "NO PATIENT CONNECTED" : null}
        </div>
        {this.store.isSnippetWorklist ||
        _.get(moduleSpecifics, "snippetPending") ? (
          snippetUpdateTime ? (
            <div className="snippet-update">
              <span>{moduleSpecifics?.snippetWorklistItemUserName}</span>
              <span>
                {formatTime(snippetUpdateTime)}
                <span className="todo done">
                  <Icon symbol="checkmark" />
                </span>
              </span>
            </div>
          ) : (
            <div className="snippet-update">
              <span className="todo"></span>
            </div>
          )
        ) : null}
      </div>
    );
  }

  orBlank(value, conversion = (d) => d) {
    return value ? conversion(value) : " ";
  }

  patientClass(patient) {
    return this.cn({
      patient: 1,
      "empty-bed": patient.isEmptyBed,
      "filtered-out": patient.isFilteredOut,
    });
  }

  listChange(listOption) {
    this.store.listChange(listOption);
  }

  selectPatient(patient, groupId, _subRoute = "") {
    // TODO: migrate to PatientStore.
    sessionStorage.setItem("asmpid", patient.asmpid);
    this.store.groupId = groupId;
    this.store.refName =
      `ref-patientCell-` + patient.asmpid + (groupId ? "-" + groupId : "");
    this.props.store.patientStore.patient = patient;
    this.props.store.patient = patient;
    sessionStorage.setItem("moduleId", this.store.moduleId);

    let subRoute = _subRoute || this.patientSubRoute;

    const moduleSpecifics = this.store.moduleSpecifics(patient);
    const moduleId = Number(this.store.moduleId);
    const listType = this.store.selectedListOption.value.type;

    // Sub-routing broken down by module, and then by listType.
    if (moduleId === MODULE_ID.PATIENT_MONITORING) {
      if (
        listType === "census" ||
        listType === "multi_patient" ||
        listType === "custombedlist"
      ) {
        subRoute = "/monitor";
      }
      if (patient.moduleSpecifics?.isArchived) {
        subRoute = "/monitor/events";
      }

      if (this.store.isSnippetWorklist) {
        if (moduleSpecifics.snippetWorklistItemUpdated) {
          return Swal.fire({
            icon: "warning",
            title: "Worklist Item Completed",
            text: "Worklist item has already been completed.",
          });
        } else {
          const ruleDate = this.getRuleDate(
            moduleSpecifics.snippetWorklistRuleTime,
          );
          subRoute = `/monitor/snippet/${encodeURIComponent(ruleDate.toISO())}`;
          sessionStorage.snippetWorklistPatient = JSON.stringify({
            asmpid: patient.asmpid,
            snippetWorklistId: moduleSpecifics.snippetWorklistId,
          });
        }
      } else {
        sessionStorage.removeItem("snippetWorklistPatient");
      }

      if (this.store.isBaselineWorklist) {
        subRoute = `/monitor/baseline`;
        sessionStorage.baselineWorklist = "true";
      } else {
        sessionStorage.removeItem("baselineWorklist");
      }
    } else if (moduleId === MODULE_ID.OBSTETRICS) {
      subRoute = "/ob-summary";
    } else if (moduleId === MODULE_ID.CARDIOLOGY) {
      if (listType === "ems") {
        subRoute = "/ecgs";
      } else if (listType === "mostrecent" || listType === "search") {
        subRoute = `/ecgs/${moduleSpecifics?.ecgId ?? ""}`;
      }
    }

    this.props.history.push(`/patient/${patient.asmpid}${subRoute ?? ""}`);
    return true;
  }

  /**
   * Takes a string and returns a DateTime object.
   * The original format is HH:mm, site-34 responds with a complete, but non-compliant, date-time.
   * Eventually, these should become ISO compliant.
   *
   * @export
   * @param {string} ruleDate HH:mm-formatted timestamp
   * @return {DateTime} A Luxon DateTime object
   */
  getRuleDate(ruleDate) {
    let res = DateTime.fromISO(ruleDate);

    if (!res.isValid) {
      res = DateTime.local();
      const matches = ruleDate.match(/(\d{2}):(\d{2})/); // find HH:mm

      res.set({ hour: matches[1], minute: matches[2] });

      if (res.isAfter(DateTime.local())) {
        res.minus({ days: 1 });
      }
    }

    return res;
  }

  search = (e) => {
    e.preventDefault();
    if (this.store.loading) return;

    const params = this.store.searchConfig.reduce((acc, field) => {
      const value = e.target[field.fieldId]?.value.trim();
      if (value) acc[field.fieldId] = value;
      return acc;
    }, {});

    // TODO: move this to patientStore.
    this.store.searchParams = params;
    this.store.searchFormKey++;
    this.store.getPatientsParams = null;
    // Check if the form has been filled in.
    if (Object.keys(params).length) {
      this.store.noSearchRequirements = false;
      this.store.searchParams = params;
      this.store.getPatients({
        ...params,
        ...this.store.selectedListOption.value.parameters,
      });
    } else {
      this.store.searchParams = null;
      this.store.noSearchRequirements = true;
      this.store.patients = [];
    }
  };

  resetSearch = () => {
    this.store.searchFormKey++;
    this.store.patients = [];
    this.store.searchParams = undefined;
    this.store.getPatientsParams = null;
  };
}

export default inject("store")(observer(Patients));

export function MyPatientsToggle({ patient, appStore, parent }) {
  if (patient.status === "discharged") {
    return null;
  }
  const ob =
    Number(appStore.patientStore.moduleId) === Number(MODULE_ID.OBSTETRICS);
  const multiPatients = _.get(
    appStore.prefs,
    ob
      ? "userPreferences.client.ob.multiPatients"
      : "userPreferences.client.pm.multiPatients",
    [],
  );

  const selected = multiPatients.includes(patient.asmpid);

  return (
    <div
      {...className({
        "multi-patient-toggle": true,
        selected,
      })}
    >
      <Icon
        symbol="star"
        onClick={(e) => {
          e.stopPropagation();
          if (selected) {
            _.remove(multiPatients, (asmpid) => asmpid === patient.asmpid);
          } else {
            multiPatients.push(patient.asmpid);
            appStore.patientStore.multiPatientNotify = patient;
            if (parent) {
              if (parent.multiPatientNotifyTimeout) {
                clearTimeout(parent.multiPatientNotifyTimeout);
              }
              parent.multiPatientNotifyTimeout = setTimeout(
                () => (appStore.patientStore.multiPatientNotify = null),
                2000,
              );
            }
          }
          appStore.setPref(
            ob ? "ob.multiPatients" : "pm.multiPatients",
            _.uniq(multiPatients),
          );
        }}
      />
    </div>
  );
}
